import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import {
  GraduationCap, Users, BookOpen, Shield, Bus, CreditCard,
  BarChart3, Globe, Download, Smartphone, Monitor, ChevronRight,
  Wifi, WifiOff, MessageSquare, FileText, Award, CheckCircle,
  ArrowRight, Star, Zap, Clock, Bot, Send, Megaphone, Video,
  FolderOpen, Layers, Phone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets-eiarnc6j.emergentagent.net/job_uni-smartsys/artifacts/y8ickh5d_IMG-20260816-WA0087.jpg';
const WHATSAPP_NUMBER = '14313345085';
const SITE_URL = 'uni.josmarttech.net';

// PWA Install Hook
const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
    }
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setIsInstalled(true));
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return outcome === 'accepted';
  }, [deferredPrompt]);

  return { deferredPrompt, isInstalled, install };
};

// Stats Counter Animation
const AnimatedCounter = ({ end, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [visible, setVisible] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const step = end / (2000 / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [visible, end]);

  return <span ref={ref}>{count.toLocaleString('fr-FR')}{suffix}</span>;
};

// Collaboration Form Component
const CollaborationForm = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', university: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.university) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API_URL}/public/collaboration-request`, form);
      toast.success('Demande envoyee avec succes ! Nous vous contacterons bientot.');
      setForm({ name: '', email: '', phone: '', university: '', message: '' });
    } catch {
      toast.error('Erreur lors de l\'envoi. Veuillez reessayer.');
    } finally { setSending(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="collaboration-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Input placeholder="Nom complet *" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40" data-testid="collab-name" />
        <Input placeholder="Email *" type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40" data-testid="collab-email" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Input placeholder="Telephone / WhatsApp" value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40" data-testid="collab-phone" />
        <Input placeholder="Nom de l'universite *" value={form.university} onChange={e => setForm(p => ({...p, university: e.target.value}))} className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40" data-testid="collab-university" />
      </div>
      <textarea placeholder="Votre message (facultatif)" value={form.message} onChange={e => setForm(p => ({...p, message: e.target.value}))} rows={3} className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder:text-white/40 text-sm resize-none" data-testid="collab-message" />
      <Button type="submit" disabled={sending} className="w-full sm:w-auto bg-[#D4AF37] hover:bg-[#B8962F] text-[#0A1628] font-bold h-12 px-8" data-testid="collab-submit">
        {sending ? 'Envoi...' : 'Demander une collaboration'} <Send className="w-4 h-4 ml-2" />
      </Button>
    </form>
  );
};

// Floating WhatsApp Button
const WhatsAppFloat = () => (
  <a
    href={`https://wa.me/${WHATSAPP_NUMBER}?text=Bonjour%20JoSmart%20Uni%2C%20je%20souhaite%20obtenir%20plus%20d%27informations%20sur%20votre%20plateforme.`}
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] hover:bg-[#1EBE57] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110 group"
    data-testid="whatsapp-float"
  >
    <svg viewBox="0 0 32 32" className="w-7 h-7 fill-white">
      <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.132 6.742 3.052 9.376L1.054 31.35l6.172-1.972A15.89 15.89 0 0016.004 32C24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.31 22.598c-.39 1.1-1.932 2.014-3.18 2.28-.854.18-1.968.324-5.72-1.23-4.802-1.988-7.892-6.858-8.132-7.176-.23-.318-1.934-2.576-1.934-4.914s1.224-3.482 1.66-3.96c.436-.478.952-.598 1.27-.598.318 0 .636.002.914.016.294.016.688-.112 1.076.82.39.94 1.326 3.232 1.442 3.466.116.234.194.508.038.82-.156.318-.234.514-.468.792-.234.278-.492.62-.702.832-.234.234-.478.488-.206.958.272.47 1.214 2.002 2.606 3.244 1.788 1.596 3.296 2.09 3.766 2.324.47.234.746.196 1.02-.118.278-.318 1.186-1.382 1.504-1.856.318-.478.636-.394 1.07-.234.436.156 2.762 1.302 3.234 1.54.47.234.784.356.9.55.116.196.116 1.128-.274 2.228z"/>
    </svg>
    <span className="absolute -top-10 right-0 bg-white text-slate-800 text-xs font-semibold px-3 py-1.5 rounded-lg shadow-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
      Contactez-nous sur WhatsApp
    </span>
  </a>
);

// Main Landing Page
export const LandingPage = () => {
  const [showInstall, setShowInstall] = useState(false);
  const { deferredPrompt, isInstalled, install } = usePWAInstall();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      const accepted = await install();
      if (accepted) toast.success('Application installee avec succes !');
      setShowInstall(false);
    } else {
      setShowInstall(true);
    }
  };

  const features = [
    { icon: GraduationCap, title: "Gestion Academique LMD", desc: "Notes, deliberations, bulletins, releves — systeme LMD complet adapte aux universites de la RDC", color: "#0F2C59" },
    { icon: Users, title: "28+ Roles Hierarchiques", desc: "Du Recteur au Chauffeur, chaque acteur dispose d'un tableau de bord adapte a ses responsabilites", color: "#059669" },
    { icon: Shield, title: "Multi-Tenant Securise", desc: "Chaque universite dispose de son espace isole avec gestion des co-administrateurs et permissions granulaires", color: "#7C3AED" },
    { icon: Video, title: "Cours en Ligne", desc: "Salles de cours virtuelles integrees pour l'enseignement a distance — un atout majeur pour les universites du Congo", color: "#0891B2" },
    { icon: Megaphone, title: "Communication Hierarchique", desc: "Annonces ciblees par institution, faculte, departement ou classe. Notifications en temps reel pour tous", color: "#D97706" },
    { icon: FolderOpen, title: "Dossiers Etudiants", desc: "Demande et suivi de documents administratifs en ligne : releves, attestations, certificats et plus", color: "#DC2626" },
    { icon: Bot, title: "Assistant IA Integre", desc: "JosAide, un assistant intelligent qui guide chaque utilisateur dans la navigation de la plateforme", color: "#EC4899" },
    { icon: Layers, title: "Gestion des Classes", desc: "Groupes de classe avec chefs elus, espaces collaboratifs et suivi en temps reel des effectifs", color: "#F97316" },
  ];

  const stats = [
    { value: 950, suffix: "+", label: "Universites Ciblees" },
    { value: 604, suffix: "K+", label: "Etudiants en RDC" },
    { value: 28, suffix: "+", label: "Roles Disponibles" },
    { value: 100, suffix: "%", label: "Conforme ESU" },
  ];

  const roles = [
    "Recteur", "Vice-Recteur", "SG Academique", "SG Administratif",
    "Doyen", "Chef de Departement", "Enseignant", "Etudiant",
    "Caissier", "Bibliothecaire", "Chauffeur", "Appariteur"
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Sticky Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-lg shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
            <img src={LOGO_URL} alt="JoSmart Uni" className="w-10 h-10 rounded-xl object-cover" />
            <span className={`font-['Manrope'] font-bold text-lg ${scrolled ? 'text-[#0A1628]' : 'text-white'}`}>
              JoSmart Uni
            </span>
          </Link>
          <div className="flex items-center gap-3">
            {!isInstalled && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleInstall}
                className={`hidden sm:flex items-center gap-2 border-2 ${scrolled ? 'border-[#0F2C59] text-[#0F2C59] hover:bg-[#0F2C59] hover:text-white' : 'border-white/40 text-white hover:bg-white/10'}`}
                data-testid="pwa-install-btn-nav"
              >
                <Download className="w-4 h-4" /> Installer
              </Button>
            )}
            <Link to="/login">
              <Button size="sm" className="bg-[#D4AF37] hover:bg-[#B8962F] text-[#0A1628] font-semibold" data-testid="landing-login-btn">
                Se connecter <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-[#0A1628]">
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: 'radial-gradient(circle at 30% 40%, #0F2C59 0%, transparent 50%), radial-gradient(circle at 70% 60%, #1a3a6e 0%, transparent 50%)'
          }} />
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(1.5px 1.5px at 20px 30px, rgba(212,175,55,0.15) 1px, transparent 0), radial-gradient(1px 1px at 50px 80px, rgba(255,255,255,0.08) 1px, transparent 0)',
            backgroundSize: '120px 120px'
          }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-0">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#D4AF37]/20 text-[#D4AF37] px-4 py-1.5 rounded-full text-sm font-medium mb-6">
                  <Zap className="w-4 h-4" /> Plateforme N.1 en RDC
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white font-['Manrope'] leading-tight">
                  La Gestion<br />
                  <span className="text-[#D4AF37]">Universitaire</span><br />
                  Reinventee
                </h1>
                <p className="text-lg text-white/70 mt-6 max-w-lg leading-relaxed">
                  JoSmart Uni digitalise entierement la gestion academique, administrative et financiere 
                  de votre universite. Conforme au systeme LMD et aux normes du Ministere de l'ESU.
                </p>
              </div>

              <div className="flex flex-wrap gap-4">
                <a href="#collaboration">
                  <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B8962F] text-[#0A1628] font-bold h-12 px-8 text-base" data-testid="hero-collab-btn">
                    Demander une collaboration <ChevronRight className="w-5 h-5 ml-1" />
                  </Button>
                </a>
                {!isInstalled && (
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={handleInstall}
                    className="border-2 border-white/30 text-white hover:bg-white/10 h-12 px-8 font-semibold text-base"
                    data-testid="hero-install-btn"
                  >
                    <Download className="w-5 h-5 mr-2" /> Installer l'App
                  </Button>
                )}
              </div>

              <div className="flex items-center gap-6 text-white/50 text-sm flex-wrap">
                <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Acces gratuit a la demo</div>
                <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Cours en ligne integres</div>
                <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Support disponible</div>
              </div>
            </div>

            {/* Hero Visual */}
            <div className="hidden lg:block">
              <div className="relative">
                <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-6 shadow-2xl">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="ml-4 text-white/40 text-xs font-mono">{SITE_URL}/dashboard</span>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-white/10 rounded-xl p-4">
                      <div className="text-white/60 text-xs mb-2">Tableau de bord — Recteur</div>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { label: "Etudiants", val: "990", color: "text-blue-300" },
                          { label: "Enseignants", val: "364", color: "text-green-300" },
                          { label: "Cours", val: "370", color: "text-purple-300" },
                        ].map((s, i) => (
                          <div key={i} className="bg-white/5 rounded-lg p-3 text-center">
                            <div className={`text-xl font-bold ${s.color}`}>{s.val}</div>
                            <div className="text-white/50 text-xs">{s.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white/10 rounded-xl p-4">
                        <div className="text-white/60 text-xs mb-2">Annonces</div>
                        <div className="text-[#D4AF37] text-lg font-bold">10</div>
                        <div className="text-white/40 text-xs">Actives ce mois</div>
                      </div>
                      <div className="bg-white/10 rounded-xl p-4">
                        <div className="text-white/60 text-xs mb-2">Classes</div>
                        <div className="text-green-400 text-lg font-bold">6</div>
                        <div className="text-white/40 text-xs">Groupes actifs</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 bg-[#D4AF37] text-[#0A1628] rounded-2xl px-4 py-2 font-bold text-sm shadow-xl">
                  <Bot className="w-4 h-4 inline mr-1" /> Assistant IA integre
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#0F2C59] py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl sm:text-4xl font-extrabold text-[#D4AF37] font-['Manrope']">
                  <AnimatedCounter end={s.value} suffix={s.suffix} />
                </div>
                <div className="text-white/60 text-sm mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-gray-50" id="features" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A1628] font-['Manrope']">
              Tout ce dont votre universite a besoin
            </h2>
            <p className="text-gray-500 mt-4 max-w-2xl mx-auto text-base">
              Une plateforme complete qui couvre tous les aspects de la gestion universitaire, 
              de l'enseignement en ligne a la gestion des classes.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <div key={i} className="group bg-white rounded-2xl p-6 border border-gray-100 hover:border-transparent hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: f.color + '15' }}>
                  <f.icon className="w-6 h-6" style={{ color: f.color }} />
                </div>
                <h3 className="font-bold text-[#0A1628] mb-2 font-['Manrope']">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Features */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: Bus, title: "Transport & GPS", desc: "Suivi en temps reel des bus universitaires et localisation sur carte", color: "#D97706" },
              { icon: CreditCard, title: "Paiement Integre", desc: "Systeme de paiement a configurer avec votre etablissement", color: "#DC2626" },
              { icon: BarChart3, title: "Analyses & Rapports", desc: "Tableaux de bord detailles et statistiques en temps reel", color: "#059669" },
            ].map((f, i) => (
              <div key={i} className="flex items-start gap-4 p-5 rounded-2xl bg-gray-50 border border-gray-100">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: f.color + '15' }}>
                  <f.icon className="w-5 h-5" style={{ color: f.color }} />
                </div>
                <div>
                  <h4 className="font-bold text-[#0A1628] text-sm mb-1">{f.title}</h4>
                  <p className="text-gray-500 text-sm">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles Section */}
      <section className="py-20 bg-gray-50" data-testid="roles-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A1628] font-['Manrope'] mb-6">
                28+ Roles<br /><span className="text-[#D4AF37]">Un tableau de bord pour chacun</span>
              </h2>
              <p className="text-gray-500 mb-8 leading-relaxed">
                Chaque acteur de l'universite — du Recteur au chauffeur de bus — dispose d'un espace 
                personnalise avec les outils et informations pertinents a son role.
              </p>
              <div className="flex flex-wrap gap-2">
                {roles.map((r, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-[#0F2C59]/8 text-[#0F2C59] px-3 py-1.5 rounded-lg text-sm font-medium">
                    <CheckCircle className="w-3.5 h-3.5 text-[#D4AF37]" /> {r}
                  </span>
                ))}
                <span className="inline-flex items-center bg-[#D4AF37]/15 text-[#B8962F] px-3 py-1.5 rounded-lg text-sm font-bold">
                  +16 autres...
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { role: "Recteur", icon: Award, items: ["Vue d'ensemble", "Annonces", "Deliberations"], color: "#0F2C59" },
                { role: "Enseignant", icon: BookOpen, items: ["Mes cours", "Notes", "Cours en ligne"], color: "#059669" },
                { role: "Etudiant", icon: GraduationCap, items: ["Ma classe", "Dossiers", "Assistant IA"], color: "#7C3AED" },
                { role: "SG Academique", icon: FileText, items: ["Inscriptions", "Programmes", "Classes"], color: "#D97706" },
              ].map((card, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 border border-gray-100 hover:shadow-lg transition-shadow">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: card.color + '15' }}>
                    <card.icon className="w-5 h-5" style={{ color: card.color }} />
                  </div>
                  <div className="font-bold text-sm text-[#0A1628] mb-2">{card.role}</div>
                  {card.items.map((item, j) => (
                    <div key={j} className="flex items-center gap-1.5 text-gray-500 text-xs mb-1">
                      <div className="w-1 h-1 rounded-full" style={{ backgroundColor: card.color }} /> {item}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Install Section */}
      <section className="py-20 bg-[#0A1628] relative overflow-hidden" data-testid="install-section">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 80% 50%, #D4AF37 0%, transparent 50%)'
        }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white font-['Manrope'] mb-6">
                Installez l'application<br />
                <span className="text-[#D4AF37]">sur n'importe quel appareil</span>
              </h2>
              <p className="text-white/60 mb-8 leading-relaxed">
                JoSmart Uni fonctionne comme une application native sur votre telephone, tablette ou ordinateur.
                Installez directement depuis votre navigateur en un clic.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  { icon: WifiOff, text: "Consultez vos donnees meme sans Internet" },
                  { icon: Clock, text: "Notifications en temps reel" },
                  { icon: Smartphone, text: "Interface native sur mobile, tablette et PC" },
                  { icon: Zap, text: "Demarrage instantane, mises a jour automatiques" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                    <span className="text-white/80 text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
              {!isInstalled ? (
                <Button
                  size="lg"
                  onClick={handleInstall}
                  className="bg-[#D4AF37] hover:bg-[#B8962F] text-[#0A1628] font-bold h-12 px-8 text-base"
                  data-testid="pwa-install-btn-section"
                >
                  <Download className="w-5 h-5 mr-2" /> Installer JoSmart Uni
                </Button>
              ) : (
                <div className="inline-flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-lg font-medium">
                  <CheckCircle className="w-5 h-5" /> Application deja installee
                </div>
              )}
            </div>
            <div className="hidden lg:flex justify-center">
              <div className="relative">
                <div className="w-64 h-[480px] bg-gray-900 rounded-[2.5rem] border-4 border-gray-700 overflow-hidden shadow-2xl">
                  <div className="w-full h-6 bg-gray-900 flex items-center justify-center">
                    <div className="w-20 h-4 bg-gray-800 rounded-full" />
                  </div>
                  <div className="bg-[#0F2C59] p-4 h-full">
                    <div className="flex items-center gap-2 mb-4">
                      <img src={LOGO_URL} alt="" className="w-8 h-8 rounded-lg" />
                      <span className="text-white font-bold text-sm">JoSmart Uni</span>
                    </div>
                    <div className="bg-white/10 rounded-xl p-3 mb-3">
                      <div className="text-white/50 text-xs">Bienvenue</div>
                      <div className="text-white font-bold text-sm">Marie Kapinga</div>
                      <div className="text-[#D4AF37] text-xs">Etudiante — L2 Informatique</div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="bg-white/10 rounded-lg p-2 text-center">
                        <div className="text-green-400 font-bold text-lg">14.5</div>
                        <div className="text-white/40 text-[10px]">Moyenne S1</div>
                      </div>
                      <div className="bg-white/10 rounded-lg p-2 text-center">
                        <div className="text-blue-400 font-bold text-lg">42</div>
                        <div className="text-white/40 text-[10px]">Credits</div>
                      </div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2">
                      <div className="text-white/50 text-[10px] mb-1">Prochain cours en ligne</div>
                      <div className="text-white text-xs font-medium">Programmation Web</div>
                      <div className="text-[#D4AF37] text-[10px]">09:30 — Salle virtuelle</div>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-8 bg-green-500 text-white px-3 py-1.5 rounded-xl text-xs font-bold shadow-lg">
                  <WifiOff className="w-3 h-3 inline mr-1" /> Hors-ligne OK
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Collaboration Section */}
      <section className="py-20 bg-[#0F2C59] relative" id="collaboration" data-testid="collaboration-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white font-['Manrope'] mb-4">
              Demander une <span className="text-[#D4AF37]">collaboration</span>
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Vous souhaitez digitaliser la gestion de votre universite ? Remplissez ce formulaire 
              et notre equipe vous contactera dans les plus brefs delais.
            </p>
          </div>
          <CollaborationForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A1628] py-12 border-t border-white/5" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO_URL} alt="JoSmart" className="w-10 h-10 rounded-xl" />
                <span className="text-white font-bold font-['Manrope']">JoSmart Uni</span>
              </div>
              <p className="text-white/40 text-sm leading-relaxed mb-3">
                Plateforme de gestion academique universitaire pour la Republique Democratique du Congo.
              </p>
              <a href={`https://${SITE_URL}`} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] text-sm hover:text-[#D4AF37]/80 font-medium" data-testid="site-link">
                <Globe className="w-3.5 h-3.5 inline mr-1" /> {SITE_URL}
              </a>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Plateforme</h4>
              <div className="space-y-2">
                {["Gestion Academique LMD", "Cours en Ligne", "Communication Hierarchique", "Gestion des Classes", "Assistant IA Integre", "Dossiers Etudiants"].map((item, i) => (
                  <div key={i} className="text-white/40 text-sm hover:text-white/70 cursor-default">{item}</div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Contact</h4>
              <div className="space-y-2">
                <a href="mailto:support@josmarttech.net" className="text-white/40 text-sm hover:text-white/70 flex items-center gap-2" data-testid="footer-email">
                  <MessageSquare className="w-3.5 h-3.5" /> support@josmarttech.net
                </a>
                <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className="text-white/40 text-sm hover:text-white/70 flex items-center gap-2" data-testid="footer-whatsapp">
                  <Phone className="w-3.5 h-3.5" /> +1 (431) 334-5085 (WhatsApp)
                </a>
                <Link to="/legal" className="text-white/40 text-sm hover:text-white/70 flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5" /> Mentions Legales
                </Link>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-white/30 text-xs">&copy; {new Date().getFullYear()} JoSmart Technologies. Tous droits reserves.</div>
            <div className="flex items-center gap-4">
              <Link to="/legal" className="text-white/30 text-xs hover:text-white/50">Confidentialite</Link>
              <Link to="/legal" className="text-white/30 text-xs hover:text-white/50">Conditions</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <WhatsAppFloat />

      {/* PWA Install Modal */}
      {showInstall && !deferredPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowInstall(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-testid="pwa-install-modal">
            <div className="bg-[#0A1628] p-6 text-center">
              <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Download className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white font-['Manrope']">Installer JoSmart Uni</h3>
              <p className="text-white/60 text-sm mt-1">Fonctionne meme hors connexion</p>
            </div>
            <div className="p-6">
              <p className="text-gray-600 text-sm mb-4">Pour installer l'application, utilisez le menu de votre navigateur :</p>
              <ol className="space-y-3">
                {[
                  "Ouvrez le menu de votre navigateur (3 points ou icone partage)",
                  "Selectionnez \"Installer l'application\" ou \"Ajouter a l'ecran d'accueil\"",
                  "Confirmez l'installation"
                ].map((step, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#0F2C59] text-white text-sm font-bold flex items-center justify-center">{i + 1}</span>
                    <span className="text-gray-700 text-sm pt-0.5">{step}</span>
                  </li>
                ))}
              </ol>
              <button onClick={() => setShowInstall(false)} className="w-full mt-6 text-sm text-gray-500 hover:text-gray-700 py-2">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
